import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserDetail, UserRole, UserUpdate } from '@smile/shared';
import { USER_ROLES } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { ROLE_LABEL } from '../lib/format';

/**
 * The legacy `settings` form — one component for an administrator editing
 * any account (`admin`), and for a person editing their own (`self`, the
 * fields the legacy let anybody change on themselves: name, e-mail, the
 * company block). The administrator's extras are the legacy's: pricelist,
 * Fakturace CK, provision, promo codes, and the status buttons.
 */
type Draft = {
  name: string;
  email: string;
  role: UserRole;
  pricelistId: string;
  invoicePayment: boolean;
  provision: string;
  promo: string;
  note: string;
  partnerCompany: string;
  partnerStreet: string;
  partnerCity: string;
  partnerPsc: string;
  partnerIc: string;
  partnerDic: string;
};

const EMPTY: Draft = {
  name: '',
  email: '',
  role: 'partner',
  pricelistId: '1',
  invoicePayment: false,
  provision: '0',
  promo: '',
  note: '',
  partnerCompany: '',
  partnerStreet: '',
  partnerCity: '',
  partnerPsc: '',
  partnerIc: '',
  partnerDic: '',
};

function fromDetail(u: UserDetail): Draft {
  return {
    name: u.name,
    email: u.email,
    role: u.role,
    pricelistId: String(u.pricelistId),
    invoicePayment: u.invoicePayment,
    provision: String(u.provision),
    promo: u.promos.join(', '),
    note: u.note,
    partnerCompany: u.partnerCompany,
    partnerStreet: u.partnerStreet,
    partnerCity: u.partnerCity,
    partnerPsc: u.partnerPsc,
    partnerIc: u.partnerIc,
    partnerDic: u.partnerDic,
  };
}

/** The changed fields, typed; null when a number is bad. */
export function userPatch(base: Draft, d: Draft, mode: 'admin' | 'self'): UserUpdate | null {
  const out: UserUpdate = {};
  if (d.name.trim() !== base.name) out.name = d.name.trim();
  if (d.email.trim().toLowerCase() !== base.email) out.email = d.email.trim().toLowerCase();
  for (const k of ['partnerCompany', 'partnerStreet', 'partnerCity', 'partnerPsc', 'partnerIc', 'partnerDic'] as const) {
    if (d[k].trim() !== base[k]) out[k] = d[k].trim();
  }
  if (mode === 'admin') {
    if (d.role !== base.role) out.role = d.role;
    const pl = Number(d.pricelistId);
    const pv = Number(d.provision);
    if (!Number.isInteger(pl) || pl < 1 || !Number.isInteger(pv) || pv < 0 || pv > 100) return null;
    if (d.pricelistId !== base.pricelistId) out.pricelistId = pl;
    if (d.provision !== base.provision) out.provision = pv;
    if (d.invoicePayment !== base.invoicePayment) out.invoicePayment = d.invoicePayment;
    if (d.promo.trim() !== base.promo) out.promo = d.promo.trim();
    if (d.note.trim() !== base.note) out.note = d.note.trim();
  }
  return out;
}

export function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}

export function UserForm({
  user,
  mode,
  onCreated,
}: {
  /** null = a new account (admin only). */
  user: UserDetail | null;
  mode: 'admin' | 'self';
  onCreated?: (id: number, password: string) => void;
}) {
  const qc = useQueryClient();
  const base = user ? fromDetail(user) : EMPTY;
  const [d, setD] = useState<Draft>(base);
  useEffect(() => setD(user ? fromDetail(user) : EMPTY), [user]);
  const lists = useQuery({ queryKey: ['catalog', 'pricelists'], queryFn: () => api.pricelists(), staleTime: 10 * 60_000, enabled: mode === 'admin' });
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD({ ...d, [k]: v });

  const save = useMutation({
    mutationFn: async () => {
      if (!user) {
        const pl = Number(d.pricelistId);
        const pv = Number(d.provision);
        return api.createUser({
          name: d.name.trim(),
          email: d.email.trim().toLowerCase(),
          role: d.role,
          pricelistId: Number.isInteger(pl) && pl >= 1 ? pl : 1,
          provision: Number.isInteger(pv) ? pv : 0,
          invoicePayment: d.invoicePayment,
          promo: d.promo.trim(),
          note: d.note.trim(),
          partnerCompany: d.partnerCompany.trim(),
          partnerStreet: d.partnerStreet.trim(),
          partnerCity: d.partnerCity.trim(),
          partnerPsc: d.partnerPsc.trim(),
          partnerIc: d.partnerIc.trim(),
          partnerDic: d.partnerDic.trim(),
        });
      }
      const patch = userPatch(base, d, mode);
      if (!patch || !Object.keys(patch).length) throw new Error('Není co uložit.');
      return mode === 'admin' ? api.updateUser(user.id, patch) : api.updateAccount(patch);
    },
    onSuccess: async (out) => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      await qc.invalidateQueries({ queryKey: ['account'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
      if (!user && 'password' in out) onCreated?.(out.id, out.password);
    },
  });
  const patch = user ? userPatch(base, d, mode) : null;
  const dirty = user ? !!patch && Object.keys(patch).length > 0 : d.name.trim().length > 0 && d.email.trim().length > 3;
  const valid = user ? patch !== null : true;
  const message = errText(save.error);
  const admin = mode === 'admin';

  return (
    <form
      className="card detailcard"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty && valid && !save.isPending) save.mutate();
      }}
    >
      <h2>{user ? 'Účet' : 'Nový účet'}</h2>
      <div className="efgrid">
        <label className="ef">
          Jméno
          <input value={d.name} maxLength={63} required onChange={(e) => set('name', e.target.value)} />
        </label>
        <label className="ef">
          E-mail
          <input type="email" value={d.email} maxLength={63} required onChange={(e) => set('email', e.target.value)} />
        </label>
        {admin && (
          <>
            <label className="ef">
              Ceník
              <select value={d.pricelistId} onChange={(e) => set('pricelistId', e.target.value)}>
                {!(lists.data ?? []).some((l) => String(l.id) === d.pricelistId) && <option value={d.pricelistId}>#{d.pricelistId}</option>}
                {(lists.data ?? []).map((l) => (
                  <option key={l.id} value={String(l.id)}>
                    #{l.id} {l.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="ef">
              Provize partnera (%)
              <input type="number" min={0} max={100} step={1} value={d.provision} onChange={(e) => set('provision', e.target.value)} />
            </label>
            <label className="ef wide2">
              Promo kódy (více oddělte čárkou)
              <input value={d.promo} maxLength={255} onChange={(e) => set('promo', e.target.value)} placeholder="např. CK10, CK20" />
            </label>
            <label className="ef" style={{ justifyContent: 'flex-end' }}>
              <span className="chk" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                <input type="checkbox" checked={d.invoicePayment} onChange={(e) => set('invoicePayment', e.target.checked)} /> Fakturace CK — parkování
                účtuje partner, klient při odjezdu neplatí
              </span>
            </label>
          </>
        )}
      </div>

      <h3 className="efh">Společnost (fakturační údaje partnera)</h3>
      <div className="efgrid">
        <label className="ef wide2">
          Společnost (firma)
          <input value={d.partnerCompany} maxLength={255} onChange={(e) => set('partnerCompany', e.target.value)} />
        </label>
        <label className="ef wide2">
          Ulice a č.p.
          <input value={d.partnerStreet} maxLength={255} onChange={(e) => set('partnerStreet', e.target.value)} />
        </label>
        <label className="ef">
          Město (obec)
          <input value={d.partnerCity} maxLength={255} onChange={(e) => set('partnerCity', e.target.value)} />
        </label>
        <label className="ef">
          PSČ
          <input value={d.partnerPsc} maxLength={8} onChange={(e) => set('partnerPsc', e.target.value)} />
        </label>
        <label className="ef">
          IČ
          <input value={d.partnerIc} maxLength={63} onChange={(e) => set('partnerIc', e.target.value)} />
        </label>
        <label className="ef">
          DIČ
          <input value={d.partnerDic} maxLength={63} onChange={(e) => set('partnerDic', e.target.value)} />
        </label>
      </div>

      {admin && (
        <>
          <h3 className="efh">Status</h3>
          <div className="rolepick" role="radiogroup" aria-label="Status účtu">
            {USER_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={d.role === r}
                className={d.role === r ? 'btn sm primary' : 'btn sm'}
                onClick={() => set('role', r)}
                disabled={!user && r === 'deleted'}
              >
                {ROLE_LABEL[r]}
              </button>
            ))}
          </div>
          <label className="ef wide" style={{ marginTop: 10 }}>
            Poznámka (jen pro správce)
            <textarea rows={2} value={d.note} maxLength={2000} onChange={(e) => set('note', e.target.value)} />
          </label>
        </>
      )}

      <div className="efactions">
        <button type="submit" className="btn primary" disabled={!dirty || !valid || save.isPending}>
          {save.isPending ? 'Ukládám…' : user ? 'Uložit' : 'Založit účet'}
        </button>
        {save.isSuccess && !dirty && <span className="arrived-mark">✔ Uloženo</span>}
        {message && (
          <span className="arrive-error" role="alert">
            {message}
          </span>
        )}
      </div>
    </form>
  );
}
